import React from 'react'
import { 
  Star,
  Pause,
  X,
  Download,
  Loader
} from 'lucide-react'

interface JobApplicant {
  id: number
  application_id: number
  display_status: string
  has_resume: boolean
  resume_filename?: string
}

interface ApplicantActionButtonsProps {
  applicant: JobApplicant
  updating: number | null
  downloading: number | null
  onShortlist: (applicationId: number) => void
  onUpdateStatus: (applicationId: number, status: string) => void
  onDownloadResume: (applicationId: number, filename?: string) => void
}

export default function ApplicantActionButtons({
  applicant,
  updating,
  downloading,
  onShortlist,
  onUpdateStatus,
  onDownloadResume
}: ApplicantActionButtonsProps) {
  return (
    <div className="flex space-x-2">
      {/* Resume Download Button */}
      {applicant.has_resume && (
        <button
          onClick={() => onDownloadResume(applicant.application_id, applicant.resume_filename)}
          disabled={downloading === applicant.application_id}
          className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded border ${
            downloading === applicant.application_id
              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              : 'bg-green-50 text-green-600 border-green-200 hover:bg-green-100'
          }`}
          title={applicant.resume_filename || 'Download Resume'}
        >
          {downloading === applicant.application_id ? (
            <Loader className="w-3 h-3 animate-spin mr-1" />
          ) : (
            <Download className="w-3 h-3 mr-1" />
          )}
          Resume
        </button>
      )}

      {/* Shortlist Button */}
      {applicant.display_status !== 'shortlisted' && (
        <button
          onClick={() => onShortlist(applicant.application_id)}
          disabled={updating === applicant.application_id}
          className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded border ${
            updating === applicant.application_id
              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              : 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100'
          }`}
        >
          {updating === applicant.application_id ? (
            <Loader className="w-3 h-3 animate-spin mr-1" />
          ) : (
            <Star className="w-3 h-3 mr-1" />
          )}
          Shortlist
        </button>
      )}

      {/* On Hold Button */}
      {applicant.display_status !== 'on_hold' && applicant.display_status !== 'rejected' && (
        <button
          onClick={() => onUpdateStatus(applicant.application_id, 'on_hold')}
          disabled={updating === applicant.application_id}
          className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded border ${
            updating === applicant.application_id
              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              : 'bg-yellow-50 text-yellow-600 border-yellow-200 hover:bg-yellow-100'
          }`}
        >
          {updating === applicant.application_id ? (
            <Loader className="w-3 h-3 animate-spin mr-1" />
          ) : (
            <Pause className="w-3 h-3 mr-1" />
          )}
          Hold
        </button>
      )}

      {/* Reject Button */}
      {applicant.display_status !== 'rejected' && (
        <button
          onClick={() => onUpdateStatus(applicant.application_id, 'rejected')}
          disabled={updating === applicant.application_id}
          className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded border ${
            updating === applicant.application_id
              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
              : 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
          }`}
        >
          {updating === applicant.application_id ? (
            <Loader className="w-3 h-3 animate-spin mr-1" />
          ) : (
            <X className="w-3 h-3 mr-1" />
          )}
          Reject
        </button>
      )}
    </div>
  )
}